import { SessionListResponse } from '../interfaces/sessions';
import { 
  SessionV2Columns, 
  SESSION_METRICS_COLUMNS, 
  CallMetricColumns,
  PROSPECT_INFO_COLUMNS,
  ReviewStatusColumns,
  SessionAgentsColumns,
  MessagingDataColumns
} from '../interfaces/table';
import { 
  ConversationType, 
  Counterpart, 
  SessionType,
  OBJECTION_COLUMNS, 
  OBJECTION_COLUMNS_TO_HUMAN_READABLE, 
  SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE 
} from '../interfaces/db';

/**
 * Checks if a session is a call
 */
export const isSessionCall = (session: SessionListResponse): boolean => {
  return session.result.get(SessionV2Columns.CONVERSATION_TYPE) === ConversationType.CALL;
};

/**
 * Checks if a session is an incoming call (creator_agent_id is null)
 */
export const isSessionIncomingCall = (session: SessionListResponse): boolean => {
  return isSessionCall(session) && !session.result.get(SessionAgentsColumns.CREATOR_AGENT_ID);
};

/**
 * Checks if a session is missed (inbound call that wasn't answered)
 */
export const isSessionMissed = (session: SessionListResponse): boolean => {
  return isSessionCall(session) && 
    Boolean(session.result.get(CallMetricColumns.IS_INBOUND)) && 
    !session.result.get(CallMetricColumns.FIRST_USER_JOINED_AT) && 
    session.result.get(SessionV2Columns.SESSION_TYPE) !== SessionType.INTAKE && 
    (!session.result.get(SessionAgentsColumns.AGENT_TYPES_STR) || !session.result.get(SessionAgentsColumns.AGENT_TYPES_STR)?.includes('VOICE_BOT'));
};

/**
 * Checks if a session was answered
 */
export const isSessionAnswered = (session: SessionListResponse): boolean => {
  const liveDuration = session.result.get(SESSION_METRICS_COLUMNS.LIVE_DURATION);
  // Handle both number and string values for live duration
  const numericLiveDuration = typeof liveDuration === 'string' ? parseFloat(liveDuration) : liveDuration;
  return Boolean(numericLiveDuration && numericLiveDuration > 0) || 
    session.result.get(SessionV2Columns.SESSION_TYPE) === SessionType.INTAKE;
};

/**
 * Gets the counterpart type for a session
 */
export const getSessionCounterpart = (session: SessionListResponse): any => {
  return session.result.get(SESSION_METRICS_COLUMNS.BEST_COUNTERPART);
};

/**
 * Checks if the session had prospect answered (not gatekeeper)
 */
export const isSessionProspectAnswered = (session: SessionListResponse): boolean => {
  return getSessionCounterpart(session) === Counterpart.NOT_GATEKEEPER;
};

/**
 * Checks if the session had gatekeeper answered
 */
export const isSessionGatekeeperAnswered = (session: SessionListResponse): boolean => {
  return getSessionCounterpart(session) === Counterpart.GATEKEEPER;
};

/**
 * Gets the user disposition for a session
 */
export const getSessionDisposition = (session: SessionListResponse): any => {
  return session.result.get(SESSION_METRICS_COLUMNS.USER_DISPOSITION);
};

/**
 * Gets the user sentiment for a session
 */
export const getSessionSentiment = (session: SessionListResponse): any => {
  return session.result.get(SESSION_METRICS_COLUMNS.USER_SENTIMENT);
};

/**
 * Gets the correct user ID for a session based on call direction
 * - If incoming call: use first_user_id if set
 * - If outbound call: use creator_agent_id
 * - Otherwise: return undefined
 */
export const getSessionUserId = (session: SessionListResponse): string | undefined => {
  if (isSessionIncomingCall(session)) {
    // For incoming calls, use first_user_id if available
    return session.result.get(SessionAgentsColumns.FIRST_USER_ID) || session.result.get(SessionV2Columns.RESOURCE_ID);
  } else {
    // For outbound calls, use creator_agent_id
    return session.result.get(SessionAgentsColumns.CREATOR_AGENT_ID) || session.result.get(SessionV2Columns.RESOURCE_ID);
  }
};

/**
 * Gets the legacy user ID (resource ID) for backward compatibility
 * This is the old way of getting user ID - use getSessionUserId instead
 * @deprecated Use getSessionUserId instead
 */
export const getSessionResourceId = (session: SessionListResponse): string | undefined => {
  return session.result.get(SessionV2Columns.RESOURCE_ID);
};

/**
 * Gets the objection string for a session (up to 3 objections)
 */
export const getSessionObjectionString = (session: SessionListResponse): string | null => {
  if (!isSessionAnswered(session)) return null;
  
  const objections = OBJECTION_COLUMNS.filter(col => {
    const number_occurences = session.result.get(col);
    return number_occurences && Number(number_occurences) > 0;
  }).map((v) => OBJECTION_COLUMNS_TO_HUMAN_READABLE.get(v) ?? SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE[v]);
  
  return objections.slice(0, 3).join(', ');
};

/**
 * Gets the call duration for a session in seconds
 */
export const getSessionCallDuration = (session: SessionListResponse): number => {
  return session.result.get(SESSION_METRICS_COLUMNS.CALL_DURATION) ?? 0;
};

/**
 * Formats call duration from seconds to MM:SS format
 */
export const formatCallDuration = (durationInSeconds: number): string => {
  if (!durationInSeconds) return '-';
  
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = Math.round(durationInSeconds % 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

/**
 * Gets the summary text for a session
 */
export const getSessionSummary = (session: SessionListResponse): string | undefined => {
  return session.result.get(SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT);
};

/**
 * Gets the rep's phone number for a session
 */
export const getSessionRepNumber = (session: SessionListResponse): string | null => {
  const agentAddress = session.result.get(SessionV2Columns.AGENT_ADDRESS);
  return agentAddress && agentAddress !== '-' ? agentAddress : null;
};

/**
 * Formats a phone number for display
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  if (phoneNumber.charAt(0) === '1') {
    return phoneNumber.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
  }
  return phoneNumber.replace(/(\d{1,4})(\d{2,4})(\d{4})/, '+$1 $2 $3');
};

/**
 * Gets the prospect name for a session
 */
export const getSessionProspectName = (session: SessionListResponse): any => {
  return session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_NAME);
};

/**
 * Gets the company name for a session
 */
export const getSessionCompanyName = (session: SessionListResponse): any => {
  return session.result.get(PROSPECT_INFO_COLUMNS.COMPANY_NAME);
};

/**
 * Gets the prospect title for a session
 */
export const getSessionProspectTitle = (session: SessionListResponse): any => {
  return session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_TITLE);
};

/**
 * Checks if a session review is open
 */
export const isSessionReviewOpen = (session: SessionListResponse): boolean | null => {
  const value = session.result.get(ReviewStatusColumns.IS_OPEN);
  return value === undefined ? null : Boolean(value);
};

/**
 * Gets the review opening user ID for a session
 */
export const getSessionReviewOpeningUserId = (session: SessionListResponse): string | undefined => {
  return session.result.get(ReviewStatusColumns.OPENING_USER_ID);
};

/**
 * Formats the session start time to a localized time string (HH:MM format)
 */
export const getSessionCallTime = (session: SessionListResponse): string => {
  return session.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Parses a JSON string of messages, returning an empty array if invalid
 */
export const getSessionMessages = (messageString: any): string[] => {
  if (!messageString) return [];
  try {
    return JSON.parse(messageString as string);
  } catch {
    return [];
  }
};

/**
 * Determines if the last message in a session was from the agent
 */
export const isSessionLastMessageFromAgent = (session: SessionListResponse): boolean => {
  const agentTimesStr = session.result.get(MessagingDataColumns.AGENT_TIMES_STR);
  const customerTimesStr = session.result.get(MessagingDataColumns.CUSTOMER_TIMES_STR);
  
  if (!agentTimesStr && !customerTimesStr) return false;
  if (!customerTimesStr) return true;
  if (!agentTimesStr) return false;
  
  const agentTimes = (agentTimesStr as string).split(',').map(Number);
  const customerTimes = (customerTimesStr as string).split(',').map(Number);
  
  if (!agentTimes.length || !customerTimes.length) return false;
  if (!agentTimes.length) return false;
  if (!customerTimes.length) return true;
  
  const lastAgentTime = agentTimes[agentTimes.length - 1];
  const lastCustomerTime = customerTimes[customerTimes.length - 1];
  
  return lastAgentTime > lastCustomerTime;
};

/**
 * Gets the last message from a session (either agent or customer)
 */
export const getSessionLastMessage = (session: SessionListResponse): string => {
  const agentBodiesStr = getSessionMessages(session.result.get(MessagingDataColumns.AGENT_BODIES_STR));
  const customerBodiesStr = getSessionMessages(session.result.get(MessagingDataColumns.CUSTOMER_BODIES_STR));
  
  if (!agentBodiesStr.length && !customerBodiesStr.length) return 'No messages';
  if (!agentBodiesStr.length) return customerBodiesStr[customerBodiesStr.length - 1];
  if (!customerBodiesStr.length) return agentBodiesStr[agentBodiesStr.length - 1];
  
  const isLastMessageFromAgent = isSessionLastMessageFromAgent(session);
  if (isLastMessageFromAgent) {
    return agentBodiesStr[agentBodiesStr.length - 1];
  }
  return customerBodiesStr[customerBodiesStr.length - 1];
}; 